import { apiClient } from '@api'

/**
 * User Service
 *
 * Service layer for managing users
 * Provides methods for CRUD operations on users
 */
class UserService {
	/**
	 * Get all users
	 * @param {Object} params - Query parameters (page, limit, status, search, company_id, role_id, sort)
	 * @returns {Promise<Object>} Users data with pagination meta
	 */
	async getAll(params = {}) {
		const queryParams = new URLSearchParams()

		if (params.page) queryParams.append('page', params.page)
		if (params.limit) queryParams.append('limit', params.limit)
		if (params.status) queryParams.append('status', params.status)
		if (params.search) queryParams.append('search', params.search)
		if (params.company_id) queryParams.append('company_id', params.company_id)
		if (params.role_id) queryParams.append('role_id', params.role_id)
		if (params.sort) queryParams.append('sort', params.sort)

		const queryString = queryParams.toString()
		const endpoint = queryString ? `/users?${queryString}` : '/users'

		return await apiClient.get(endpoint)
	}

	/**
	 * Get user by ID
	 * @param {string|number} id - User ID
	 * @returns {Promise<Object>} User data
	 */
	async getById(id) {
		return await apiClient.get(`/users/${id}`)
	}

	/**
	 * Get users by company
	 * @param {string|number} companyId - Company ID
	 * @returns {Promise<Object>} Users data
	 */
	async getByCompany(companyId) {
		return await apiClient.get(`/users?company_id=${companyId}`)
	}

	/**
	 * Get users by role
	 * @param {string|number} roleId - Role ID
	 * @returns {Promise<Object>} Users data
	 */
	async getByRole(roleId) {
		return await apiClient.get(`/users?role_id=${roleId}`)
	}

	/**
	 * Get users by role (for team member selection)
	 * Uses company context from auth
	 * @param {string|number} roleId - Role ID
	 * @returns {Promise<Object>} Users data with { status, count, data }
	 */
	async getUsersByRole(roleId) {
		// Get company_id from user data in sessionStorage or localStorage
		let userData = null
		try {
			// Try sessionStorage first (default)
			userData = JSON.parse(sessionStorage.getItem('auth_user_data') || 'null')
			// If not in session storage, try localStorage (rememberMe=true)
			if (!userData) {
				userData = JSON.parse(localStorage.getItem('auth_user_data') || 'null')
			}
		} catch (error) {
			console.error('Error parsing user data:', error)
		}

		const companyId = userData?.company_id

		if (!companyId) {
			throw new Error('Company ID not found in authentication data. Please log in again.')
		}

		return await apiClient.get(`/users/by-role/${roleId}?company_id=${companyId}`)
	}

	/**
	 * Create new user
	 * @param {Object} userData - User data
	 * @returns {Promise<Object>} Created user data
	 */
	async create(userData) {
		return await apiClient.post('/users', userData)
	}

	/**
	 * Update user
	 * @param {string|number} id - User ID
	 * @param {Object} userData - Updated user data
	 * @returns {Promise<Object>} Updated user data
	 */
	async update(id, userData) {
		return await apiClient.put(`/users/${id}`, userData)
	}

	/**
	 * Delete user (soft delete)
	 * @param {string|number} id - User ID
	 * @returns {Promise<Object>} Success response
	 */
	async delete(id) {
		return await apiClient.delete(`/users/${id}`)
	}

	/**
	 * Update user status
	 * @param {string|number} id - User ID
	 * @param {string} status - New status (active, inactive, suspended)
	 * @returns {Promise<Object>} Updated user data
	 */
	async updateStatus(id, status) {
		return await apiClient.put(`/users/${id}/status`, { status })
	}

	/**
	 * Reset user password (admin action - no current password required)
	 * @param {string|number} id - User ID
	 * @param {string} newPassword - New password
	 * @returns {Promise<Object>} Success response
	 */
	async resetPassword(id, newPassword) {
		return await apiClient.put(`/users/${id}/admin-reset-password`, { new_password: newPassword })
	}

	/**
	 * Restore archived user
	 * @param {string|number} id - User ID
	 * @returns {Promise<Object>} Restored user data
	 */
	async restore(id) {
		return await apiClient.post(`/users/${id}/restore`, {})
	}

	/**
	 * Block user (set status to suspended)
	 * @param {string|number} id - User ID
	 * @returns {Promise<Object>} Updated user data
	 */
	async blockUser(id) {
		return await this.updateStatus(id, 'suspended')
	}

	/**
	 * Unblock user (set status to active)
	 * @param {string|number} id - User ID
	 * @returns {Promise<Object>} Updated user data
	 */
	async unblockUser(id) {
		return await this.updateStatus(id, 'active')
	}

	/**
	 * Get all companies (for dropdown filters)
	 * @returns {Promise<Object>} Companies data
	 */
	async getCompanies() {
		return await apiClient.get('/companies')
	}

	/**
	 * Get all roles (for dropdown filters)
	 * @returns {Promise<Object>} Roles data
	 */
	async getRoles() {
		return await apiClient.get('/roles')
	}
}

// Export singleton instance
export const userService = new UserService()
export default userService
